import { createAsyncThunk } from '@reduxjs/toolkit';
import { getErrorData, type IErrorData } from '@/utils/getErrorData';
import { type IUpdateRecipientAndDeliveryResponse } from '@/types/IFetchInfoOrderRecipient';
import { updateRecipientAndDelivery } from '@/hooks/orderOfferApi';
import { toast } from 'react-toastify';
import { isError } from '@/utils/typeguards/isError';
import { type IOrderRecipient } from '@/types/IOrder';
import { type DeliveryInfo } from '@/types/FunctionalModal';
import { getIsEqual } from '@/utils/isEqual';
import { type ProviderEnum, type WayToGetEnum } from '@/types/ProvidersEnum';

export interface IUpdateRecipientAndDeliveryParams {
  orderId: number;
  orderUuid: string;
  editedValues: {
    name: string;
    phone: string;
    email: string;
    address: string;
  };
  deliveryInfo: DeliveryInfo;
  localDeliveryInfo: DeliveryInfo;
  originalOrder: {
    orderRecipient: IOrderRecipient;
    delivery_provider: ProviderEnum | null;
    delivery_type: WayToGetEnum | null;
  };
}

export const updateRecipientAndDeliveryThunk = createAsyncThunk<
  IUpdateRecipientAndDeliveryResponse,
  IUpdateRecipientAndDeliveryParams,
  { rejectValue: IErrorData }
>(
  'recipientEdit/updateRecipientAndDelivery',
  async (params, { rejectWithValue }) => {
    const {
      orderId,
      editedValues,
      deliveryInfo,
      localDeliveryInfo,
      originalOrder
    } = params;

    try {
      const recipient = Object.fromEntries(
        Object.entries({
          name: editedValues.name,
          phone: editedValues.phone,
          email: editedValues.email === '' ? null : editedValues.email,
          address: deliveryInfo.pvz
        }).filter(([key, value]) => {
          if (key === 'email') {
            return true;
          }
          return (
            value !== undefined &&
            value !== null &&
            value !==
              originalOrder.orderRecipient[
                key as keyof typeof originalOrder.orderRecipient
              ]
          );
        })
      );

      const delivery = Object.fromEntries(
        Object.entries({
          provider: localDeliveryInfo.provider,
          type: localDeliveryInfo.type
        }).filter(([key, value]) => {
          if (key === 'provider') {
            return value !== originalOrder.delivery_provider;
          }
          if (key === 'type') {
            return value !== originalOrder.delivery_type;
          }
          return false;
        })
      );

      const originalRecipient = {
        name: originalOrder.orderRecipient.name,
        phone: originalOrder.orderRecipient.phone,
        email: originalOrder.orderRecipient.email ?? null,
        address:
          originalOrder.orderRecipient.orderRecipientAddress?.address ?? ''
      };

      const currentRecipient = {
        name: editedValues.name,
        phone: editedValues.phone,
        email: editedValues.email === '' ? null : editedValues.email,
        address:
          deliveryInfo.pvz !== ''
            ? deliveryInfo.pvz
            : (originalOrder.orderRecipient.orderRecipientAddress?.address ??
              '')
      };

      const originalDelivery = {
        provider: originalOrder.delivery_provider,
        type: originalOrder.delivery_type
      };

      const currentDelivery = {
        provider:
          localDeliveryInfo.provider !== ''
            ? localDeliveryInfo.provider
            : originalOrder.delivery_provider,
        type:
          localDeliveryInfo.type !== ''
            ? localDeliveryInfo.type
            : originalOrder.delivery_type
      };

      if (
        getIsEqual(originalRecipient, currentRecipient) &&
        getIsEqual(originalDelivery, currentDelivery)
      ) {
        toast('Нужно внести изменения', { type: 'info' });
        return rejectWithValue({
          message: 'Нужно внести изменения'
        });
      }

      const response = await updateRecipientAndDelivery(
        orderId,
        recipient,
        delivery
      );

      if (isError(response)) {
        return rejectWithValue(response);
      }

      toast('Данные успешно обновлены', { type: 'success' });

      return response;
    } catch (error) {
      const errorData = getErrorData(error);
      toast.error(
        errorData.message ?? 'Произошла ошибка при сохранении данных получателя'
      );
      return rejectWithValue(errorData);
    }
  }
);
